/**
 * Stable public facade for the communication API.
 *
 * Analytics calls are isolated so one temporary endpoint failure cannot reject
 * the Promise.all used by the dashboard and hide the other successful section.
 */
// Keep this list explicit. The project's current CRA/Webpack toolchain does not
// reliably expose named imports through a wildcard facade when this module also
// overrides two of those names locally.
export {
  getStandaloneMeetings,
  createStandaloneMeeting,
  getMeetingContext,
  createSharedMeetingLink,
  revokeSharedMeetingLink,
  getMeetingLobby,
  decideMeetingLobbyRequest,
  requestSharedMeetingEntry,
  sendMeetingTranscriptChunk,
  sendGuestMeetingTranscriptChunk,
  getMeetingTranscript,
  getGuestMeetingTranscript,
  getSharedMeetingEntryStatus,
  claimSharedMeetingEntry,
  createConnectMeetingInvitation,
  revokeConnectMeetingInvitation,
  getConnectMeetingSpeakingQueue,
  getConnectMeetingPolls,
  createConnectMeetingPoll,
  openConnectMeetingPoll,
  answerConnectMeetingPoll,
  closeConnectMeetingPoll,
  releaseConnectMeetingPollResults,
  getConnectMeetingQuestions,
  submitConnectMeetingQuestion,
  approveConnectMeetingQuestion,
  answerConnectMeetingQuestion,
  mergeConnectMeetingQuestion,
  withdrawConnectMeetingQuestion,
  admitConnectGuestMeeting,
  getConnectGuestMeetingContext,
  getConnectGuestMeetingMediaParticipants,
  sendConnectGuestMeetingMediaSignal,
  pollConnectGuestMeetingMediaSignals,
  getConnectGuestMeetingPolls,
  answerConnectGuestMeetingPoll,
  getConnectGuestMeetingQuestions,
  submitConnectGuestMeetingQuestion,
  withdrawConnectGuestMeetingQuestion,
  setConnectOwnRaisedHand,
  clearConnectRaisedHand,
  getConnectMeetingHelpRequests,
  requestConnectMeetingHelp,
  withdrawConnectMeetingHelp,
  resolveConnectMeetingHelp,
  getConnectPrivateNote,
  saveConnectPrivateNote,
  getConnectSharedDocuments,
  createConnectSharedDocument,
  updateConnectSharedDocument,
  setConnectSharedDocumentEditing,
  getConnectSharedDocumentHistory,
  getConnectSharedDocumentComments,
  addConnectSharedDocumentComment,
  getConnectWhiteboards,
  createConnectWhiteboard,
  getConnectWhiteboardOperations,
  addConnectWhiteboardOperation,
  createConnectWhiteboardExport,
  getConnectPostReactions,
  setConnectPostReaction,
  getConnectChannelManagement,
  getConnectChannelMentionTargets,
  getConnectChannelBookmarks,
  setConnectPostBookmark,
  getConnectChannelReadingState,
  markConnectChannelPostSeen,
  addConnectChannelMember,
  batchAddConnectBreakoutMembers,
  removeConnectChannelMember,
  archiveConnectChannel,
  getConnectChannelCapabilities,
  getMeetingContextForConversation,
  createMeetingContext,
  changeMeetingState,
  getConnectMeetingMediaParticipants,
  getConnectMeetingMediaIce,
  getMeetingCalendarFile,
  getCallCosts,
  getConnectGuestMeetingMediaIce,
  sendConnectMeetingMediaSignal,
  pollConnectMeetingMediaSignals,
  getConnectChannelWorkspace,
  createConnectChannel,
  getConnectChannelPosts,
  sendConnectChannelPost,
  editConnectChannelPost,
  withdrawConnectChannelPost,
  getConnectChannelDraft,
  saveConnectChannelDraft,
  leaveConnectChannel,
  getCoachKnowledge,
  reviewCoachKnowledge,
  getLiveCoach,
  setLiveCoachMode,
  sendLiveCoachFeedback,
  getTrainingExamples,
  createTrainingExample,
  editTrainingExample,
  reviewTrainingExample,
  getTrainingDatasets,
  getTrainingUses,
  registerTrainingUse,
  updateTrainingUse,
  getTrainingEvaluationCases,
  getTrainingEvaluations,
  getTrainingEvaluation,
  saveTrainingEvaluation,
  getTrainingDatasetReport,
  createTrainingDataset,
  exportTrainingDataset,
  createAlias,
  getAlias,
  getAliasByCode,
  getAliasByCustomer,
  lookupOrCreateAlias,
  getUnifiedInbox,
  backfillActorLinks,
  createConversation,
  getConversation,
  getConversationMailPolicy,
  getConversationsForProcess,
  getConversationsForCustomer,
  getActorConversationHistory,
  getActorCommunicationTimeline,
  getSalesMailbox,
  getConversationMessages,
  getConversationMessageAnchor,
  getTwilioWhatsAppTemplates,
  sendMessage,
  updateMessageTriage,
  setMessageReaction,
  updateConversationTriage,
  createGroupChat,
  getGroupParticipants,
  addGroupParticipant,
  removeGroupParticipant,
  broadcastToGroup,
  getConversationRepairLinks,
  lookupCustomer,
  mergeCustomerContacts,
  getBotTemplateCoverage,
  getBotTemplateAttention,
  updateBotTemplatePolicy,
  updateBotStepTemplate,
  getConversationCustomer,
  relinkConversationCustomer,
  uploadConversationAttachment,
  transcribeAndEnhanceAudio,
  transcribeMessageAudioAttachment,
  enhanceTranscribedAudioText,
  suggestConversationReply,
  proofreadText,
  translateConversation,
  getCommunicationEventLogs,
  resolveCommunicationEventLog,
  resolveAllCommunicationEventLogs,
  resolveCommunicationAttachmentUrl,
  getCommunicationAttachmentBlob,
  markConversationAsRead,
  closeConversation,
  hideMessage,
  sendEmailFromTunnel,
  sendSms,
  getTwilioSmsConfig,
  sendWhatsApp,
  sendFacebook,
  sendInstagram,
  initiateCall,
  getGlobalVoiceToken,
  voiceAgentHeartbeat,
  voiceAgentLeave,
  getVoiceMobileConfig,
  saveVoicePushDevice,
  disableVoicePushDevice,
  getVoiceCallOffer,
  getVoiceToken,
  logBrowserCall,
  getCallRecording,
  getCallTranscript,
  getMissedCallsCount,
  getMissedCalls,
  getMyCallOutcome,
  markMissedCallRead,
  markAllMissedCallsRead,
  getMissedCallCallbacks,
  setConversationOwner,
  markMissedCallCallbackDone,
  callerLookup,
  getContactNotes,
  createContactNote,
  getRecordingProxyUrl,
  transcribeCallRecording,
  generateCallCoachFeedback,
  getCallGroupFeedback,
  addCallGroupFeedback,
  getCallHistory,
  getVapiStatus,
  saveVapiConfig,
  generateAiResponseDraft,
  submitAiResponseFeedback,
  getLearningRecommendations,
  approveLearningRecommendation,
  getGlobalChatbotSettings,
  updateGlobalChatbotSettings,
  getChatbotTrainingTemplates,
  updateChatbotTrainingTemplate,
  resetChatbotTrainingTemplate,
  getChatbotTrainingKnowledge,
  createChatbotTrainingKnowledge,
  updateChatbotTrainingKnowledge,
  deleteChatbotTrainingKnowledge,
  testChatbotTraining,
  getConversationChatbotStatus,
  toggleConversationChatbot,
  getCallsByPhone,
  dialDirect,
  getStats,
  ChannelType,
  ConversationStatus,
  MessageDirection,
  scheduleMessage,
  getScheduledMessages,
  cancelScheduledMessage,
  updateScheduledMessage,
} from './communicationApiBase';
export { default } from './communicationApiBase';
export { correctMeetingTranscript, searchMeetingTranscripts, updateMeetingBriefing, confirmMeetingGoal, removeMeetingMember, getMeetingChat, sendMeetingChat, getGuestMeetingChat, sendGuestMeetingChat, getSharedMeetingLink, deleteMeeting, setMeetingSelfName, getMeetingAnalytics, setMeetingMode, grantStageSpeaker, revokeStageSpeaker, setStageHand, setGuestStageHand } from './communicationApiBase';

import {
  getCallAnalytics as getCallAnalyticsRaw,
  getMessageAnalytics as getMessageAnalyticsRaw,
} from './communicationApiBase';

const emptyCallAnalytics = (groupBy, error) => ({
  summary: {
    total: 0,
    answered: 0,
    missed: 0,
    inbound: 0,
    outbound: 0,
    total_talk_seconds: 0,
    answer_rate: 0,
    avg_duration_seconds: 0,
  },
  timeline: [],
  by_hour: [],
  by_weekday: [],
  by_customer: [],
  best_hour: null,
  group_by: groupBy,
  _analytics_error: true,
  _analytics_error_type: error?.response?.status ? `http_${error.response.status}` : 'request_failed',
});

const emptyMessageAnalytics = (groupBy, error) => ({
  summary: { total: 0, inbound: 0, outbound: 0, failed: 0 },
  by_channel: [],
  timeline: [],
  channels: [],
  group_by: groupBy,
  _analytics_error: true,
  _analytics_error_type: error?.response?.status ? `http_${error.response.status}` : 'request_failed',
});

export const getCallAnalytics = async (...args) => {
  try {
    return await getCallAnalyticsRaw(...args);
  } catch (error) {
    console.error('[Communication Analytics] Call analytics request failed:', error);
    return emptyCallAnalytics(args[0] || 'day', error);
  }
};

export const getMessageAnalytics = async (...args) => {
  try {
    return await getMessageAnalyticsRaw(...args);
  } catch (error) {
    console.error('[Communication Analytics] Message analytics request failed:', error);
    return emptyMessageAnalytics(args[0] || 'day', error);
  }
};
